import React, { useEffect, useState } from 'react';
import { Alert, Snackbar, AlertTitle, Button } from '@mui/material';
import { useAppSelector } from '../hooks/redux';
import { config } from '../config';

/**
 * Component to show subscription status warnings
 * Displays alerts when subscription is in grace period or past due
 */
const SubscriptionStatusAlert: React.FC = () => {
  const { isAuthenticated, isAdmin, token, orgId } = useAppSelector((state) => state.auth);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);

  useEffect(() => {
    // Only check for authenticated non-admin users
    if (!isAuthenticated || isAdmin) {
      setShowWarning(false);
      return;
    }

    // Only proceed if we have an org_id
    if (!orgId) {
      console.warn('[SubscriptionStatusAlert] No org_id available, skipping subscription check');
      return;
    }

    // Check subscription status on mount
    const checkSubscriptionStatus = async () => {
      try {

        // Fetch subscription status from backend (which will proxy to billing service)
        const backendUrl = config.server.backendUrl || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/admin/subscription-status?org_id=${orgId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const sub = data.subscription;

          if (!sub) return;

          // Check for grace period
          if (sub.status === 'grace_period' && sub.gracePeriodEndDate) {
            const now = Math.floor(Date.now() / 1000);
            const days = Math.ceil((sub.gracePeriodEndDate - now) / (24 * 60 * 60));

            if (days > 0) {
              setDaysRemaining(days);
              setWarningMessage(
                `Your subscription payment failed. You have ${days} day${days > 1 ? 's' : ''} remaining before your account is suspended. Please update your payment method.`
              );
              setShowWarning(true);
            }
          }

          // Check for past due
          if (sub.status === 'past_due') {
            setWarningMessage(
              'Your subscription payment is past due. Please update your payment method to avoid service interruption.'
            );
            setShowWarning(true);
          }

          // Check for canceling soon
          if (sub.cancelAtPeriodEnd && sub.currentPeriodEnd) {
            const now = Math.floor(Date.now() / 1000);
            const days = Math.ceil((sub.currentPeriodEnd - now) / (24 * 60 * 60));

            if (days > 0 && days <= 7) {
              setWarningMessage(
                `Your subscription will end in ${days} day${days > 1 ? 's' : ''}. Renew to continue using the service.`
              );
              setShowWarning(true);
            }
          }
        }
      } catch (error) {
        console.error('[SubscriptionStatusAlert] Error checking subscription:', error);
        // Don't show errors to user - this is just a warning feature
      }
    };

    checkSubscriptionStatus();
  }, [isAuthenticated, isAdmin, token, orgId]);

  const handleClose = () => {
    setShowWarning(false);
  };

  if (!showWarning) {
    return null;
  }

  return (
    <Snackbar
      open={showWarning}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ mt: 8 }}
    >
      <Alert
        severity="warning"
        onClose={handleClose}
        sx={{ minWidth: 400 }}
      >
        <AlertTitle>Subscription Warning</AlertTitle>
        {warningMessage}
        {daysRemaining !== null && daysRemaining <= 1 && (
          <Button
            size="small"
            color="inherit"
            sx={{ mt: 1 }}
            onClick={() => {
              window.open('mailto:admin@yourcompany.com?subject=Subscription Payment Issue', '_blank');
            }}
          >
            Contact Administrator
          </Button>
        )}
      </Alert>
    </Snackbar>
  );
};

export default SubscriptionStatusAlert;
